import { RE2JS } from 're2js';

/**
 * Pure helpers for merging attribute definitions queries of several widgets into shared requests
 * (`attributeNameFilter.mustMatchAny`) and splitting the merged result back per widget, emulating
 * what the backend returns for each widget's own request.
 */

export type DefinitionsBatchRequest = {
  projectIds: string[];
  experimentIds: string[];
  patterns: string[];
};

export type DefinitionsBatch<R extends DefinitionsBatchRequest> = {
  projectIds: string[];
  experimentIds: string[];
  patterns: string[];
  requests: R[];
};

/** Attribute name > whether the backend ranks it as an exact match of the pattern. */
export type PatternMatches = ReadonlyMap<string, boolean>;

export type MatchedPattern = {
  pattern: string;
  matches: PatternMatches;
};

export type DefinitionsBatchSplit<R extends DefinitionsBatchRequest> =
  | { status: 'split'; namesByRequest: Map<R, string[]> }
  | { status: 'too-many-evaluations' }
  | { status: 'unmatched-names'; unmatchedNameCount: number };

/** Lets a long computation give the event loop a turn now and then. */
export type TimeSlicer = {
  /** Whether the current time slice is used up. */
  shouldYield: () => boolean;
  /** Gives the event loop a turn and starts the next time slice. */
  yieldToEventLoop: () => Promise<void>;
};

// ClickhouseQuerySettings.MAX_QUERY_SIZE: larger definitions queries fail with a 400.
export const SERVER_MAX_QUERY_SIZE_BYTES = 256 * 1024 - 500;
// About 78% of the server limit, a margin for template and log_comment changes on the server.
export const DEFINITIONS_QUERY_SIZE_BUDGET_BYTES = 200 * 1024;
// Query template, settings, log_comment and the search after clause with an escaped
// MAX_PATH_BYTES (1024) path; the server itself reserves about 3.3 KB for these.
const QUERY_FIXED_BYTES = 6_000;
// `(project_id = <id>::UInt128 and run_id in [...]) or `
const PROJECT_FILTER_BYTES = 100;
// `<up to 39 digits of the run UUID>::UInt128,`
const RUN_ID_BYTES = 50;
// `(match(path, ...)) or ` in the filter and `lengthUTF8(extract(path,...)),` in the ranking.
const REGEX_SQL_WRAPPER_BYTES = 32;

// Server default `nextPage.limit` of the definitions query, used by the unbatched path.
const DEFINITIONS_QUERY_DEFAULT_PAGE_SIZE = 1_000;
// AttributeNameFilterSqlBuilder.shouldSkipExactMatchPriority
const SKIP_EXACT_MATCH_PRIORITY_RUN_COUNT = 100;
const canSkipExactMatchPriority = (pattern: string) => pattern.includes('.*');

const EMPTY_MATCHES: PatternMatches = new Map();

export function getRunSelectionKey({
  projectIds,
  experimentIds,
}: Pick<DefinitionsBatchRequest, 'projectIds' | 'experimentIds'>): string {
  return JSON.stringify([toSortedUnique(projectIds), toSortedUnique(experimentIds)]);
}

/**
 * Upper bound of the UTF-8 size of the SQL the backend builds for a definitions query
 * (ProjectRunFieldRepositoryImpl.searchDefinitions).
 */
export function estimateDefinitionsQuerySizeBytes({
  projectIds,
  experimentIds,
  patterns,
}: DefinitionsBatchRequest): number {
  return (
    estimateRunSelectionSqlBytes({ projectIds, experimentIds }) +
    sum([...new Set(patterns)].map(estimatePatternSqlBytes))
  );
}

function estimateRunSelectionSqlBytes({
  projectIds,
  experimentIds,
}: Pick<DefinitionsBatchRequest, 'projectIds' | 'experimentIds'>): number {
  return (
    QUERY_FIXED_BYTES +
    new Set(projectIds).size * PROJECT_FILTER_BYTES +
    new Set(experimentIds).size * RUN_ID_BYTES
  );
}

/**
 * Every pattern is rendered twice, in the filter (`match`) and in the exact match ranking
 * (`extract`), as an escaped SQL string of `(?i)` + AttributeNameFilterSqlBuilder.hotfixRegex.
 */
export function estimatePatternSqlBytes(pattern: string): number {
  return 2 * (REGEX_SQL_WRAPPER_BYTES + getSqlStringLiteralBytes(toServerRegex(pattern)));
}

function toServerRegex(pattern: string): string {
  const hotfixedPattern =
    pattern.startsWith('^') && !pattern.includes('|') ? `^\\b\\B$|${pattern}` : pattern;

  return `(?i)${hotfixedPattern}`;
}

// ClickHouseUtils.escape quotes the value and escapes only `'` and `\`.
function getSqlStringLiteralBytes(value: string): number {
  return 2 + getUtf8Bytes(value) + (value.match(/['\\]/g)?.length ?? 0);
}

function getUtf8Bytes(value: string): number {
  let bytes = 0;

  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;

    if (codePoint < 0x80) {
      bytes += 1;
    } else if (codePoint < 0x800) {
      bytes += 2;
    } else if (codePoint < 0x10000) {
      bytes += 3;
    } else {
      bytes += 4;
    }
  }

  return bytes;
}

/**
 * Groups requests by their run selection (projects and experiments compared as sets) and splits
 * each group, in arrival order, into batches under the pattern count and query size limits.
 * Patterns shared by several requests are queried once.
 */
export function chunkDefinitionsRequests<R extends DefinitionsBatchRequest>(
  requests: R[],
  { maxPatterns, maxQueryBytes }: { maxPatterns: number; maxQueryBytes: number },
): DefinitionsBatch<R>[] {
  const requestsByRunSelection = new Map<string, R[]>();

  for (const request of requests) {
    const key = getRunSelectionKey(request);
    const group = requestsByRunSelection.get(key);

    if (group) {
      group.push(request);
    } else {
      requestsByRunSelection.set(key, [request]);
    }
  }

  const batches: DefinitionsBatch<R>[] = [];

  for (const group of requestsByRunSelection.values()) {
    const projectIds = toSortedUnique(group[0].projectIds);
    const experimentIds = toSortedUnique(group[0].experimentIds);
    const runSelectionBytes = estimateRunSelectionSqlBytes({ projectIds, experimentIds });
    let batch: DefinitionsBatch<R> | undefined;
    let batchPatterns = new Set<string>();
    let batchBytes = runSelectionBytes;

    for (const request of group) {
      const newPatterns = [...new Set(request.patterns)].filter(
        (pattern) => !batchPatterns.has(pattern),
      );
      const newPatternsBytes = sum(newPatterns.map(estimatePatternSqlBytes));

      if (
        batch === undefined ||
        batchPatterns.size + newPatterns.length > maxPatterns ||
        batchBytes + newPatternsBytes > maxQueryBytes
      ) {
        const patterns = [...new Set(request.patterns)];
        batch = { projectIds, experimentIds, patterns, requests: [request] };
        batchPatterns = new Set(patterns);
        batchBytes = runSelectionBytes + sum(patterns.map(estimatePatternSqlBytes));
        batches.push(batch);
      } else {
        batch.patterns.push(...newPatterns);
        batch.requests.push(request);
        newPatterns.forEach((pattern) => batchPatterns.add(pattern));
        batchBytes += newPatternsBytes;
      }
    }
  }

  return batches;
}

/**
 * Splits the complete result of a batch into the result of each request, as the unbatched
 * resolution would return it for the request's own patterns. Doesn't split when it is too
 * expensive (more than `maxEvaluations` regex evaluations would be needed) or can't be trusted
 * (re2js doesn't match a name that the server returned). All or nothing: telling that no name is
 * unmatched needs every pattern of the batch evaluated.
 */
export async function splitDefinitionsBatch<R extends DefinitionsBatchRequest>(
  batch: DefinitionsBatch<R>,
  names: string[],
  {
    maxAttributes,
    maxEvaluations,
    slicer,
  }: { maxAttributes: number; maxEvaluations: number; slicer: TimeSlicer },
): Promise<DefinitionsBatchSplit<R>> {
  const uniqueNames = [...new Set(names)];
  const matchesByPattern = await matchNamesByPattern(uniqueNames, batch.patterns, {
    maxEvaluations,
    slicer,
  });

  if (!matchesByPattern) {
    return { status: 'too-many-evaluations' };
  }

  const matchedNames = new Set(matchesByPattern.flatMap((matches) => [...matches.keys()]));
  const unmatchedNameCount = uniqueNames.filter((name) => !matchedNames.has(name)).length;

  if (unmatchedNameCount > 0) {
    return { status: 'unmatched-names', unmatchedNameCount };
  }

  const matchesByPatternValue = new Map(
    batch.patterns.map((pattern, index) => [pattern, matchesByPattern[index]]),
  );
  // An upper bound of the server's count, used only for requests whose order doesn't depend on it
  // (hasAmbiguousExactMatchRanking).
  const runCount = new Set(batch.experimentIds).size;
  const namesByRequest = new Map<R, string[]>();

  for (const request of batch.requests) {
    if (slicer.shouldYield()) {
      await slicer.yieldToEventLoop();
    }

    namesByRequest.set(
      request,
      selectWidgetAttributeNames(
        [...new Set(request.patterns)].map((pattern) => ({
          pattern,
          matches: matchesByPatternValue.get(pattern) ?? EMPTY_MATCHES,
        })),
        { runCount, maxAttributes },
      ),
    );
  }

  return { status: 'split', namesByRequest };
}

/**
 * Matches names like the backend does it: RE2, case-insensitive, partial match. A name is an
 * exact match when ClickHouse `extract` (the first capturing group if the pattern has any, else
 * the whole match) returns the whole name. Returns undefined when more than `maxEvaluations`
 * regex evaluations would be needed, which is known before the first one, and throws when a
 * pattern doesn't compile. Yields to the event loop between evaluations when the slicer says so.
 */
export async function matchNamesByPattern(
  names: readonly string[],
  patterns: readonly string[],
  { maxEvaluations, slicer }: { maxEvaluations: number; slicer: TimeSlicer },
): Promise<PatternMatches[] | undefined> {
  const nameKeys = names.map(toCaseInsensitiveKey);
  const candidatesByPattern: Array<{ compiledPattern: RE2JS; nameIndexes: number[] }> = [];
  let evaluations = 0;

  for (const pattern of patterns) {
    if (slicer.shouldYield()) {
      await slicer.yieldToEventLoop();
    }

    const compiledPattern = compileCaseInsensitivePattern(pattern);

    if (!compiledPattern) {
      throw new Error('A pattern does not compile');
    }

    const literal = getRequiredLiteral(pattern);
    const literalKey = literal === undefined ? undefined : toCaseInsensitiveKey(literal);
    const nameIndexes: number[] = [];

    for (let index = 0; index < names.length; index += 1) {
      if (literalKey === undefined || nameKeys[index].includes(literalKey)) {
        nameIndexes.push(index);
      }
    }

    evaluations += nameIndexes.length;

    if (evaluations > maxEvaluations) {
      return undefined;
    }

    candidatesByPattern.push({ compiledPattern, nameIndexes });
  }

  const matchesByPattern: PatternMatches[] = [];

  for (const { compiledPattern, nameIndexes } of candidatesByPattern) {
    const matches = new Map<string, boolean>();

    for (const index of nameIndexes) {
      if (slicer.shouldYield()) {
        await slicer.yieldToEventLoop();
      }

      const name = names[index];
      const matcher = compiledPattern.matcher(name);

      if (matcher.find()) {
        const extracted = matcher.groupCount() > 0 ? matcher.group(1) : matcher.group(0);
        matches.set(name, extracted === name);
      }
    }

    matchesByPattern.push(matches);
  }

  return matchesByPattern;
}

/**
 * Returns what the unbatched resolution returns for a widget, given all names matching each of
 * its patterns (in source order):
 * - one pattern: the server order of its own query, cut where the unbatched paging stops,
 * - several patterns and a complete combined result: the server order of the combined query,
 *   grouped by the first matching pattern,
 * - several patterns and a cut off combined result: the server order of each pattern's own
 *   query, one pattern after another, until `maxAttributes` names are collected.
 */
export function selectWidgetAttributeNames(
  patterns: MatchedPattern[],
  { runCount, maxAttributes }: { runCount: number; maxAttributes: number },
): string[] {
  const unbatchedResultLimit = getUnbatchedResultLimit(maxAttributes);
  const names = sortLikeDefinitionsQuery(patterns, runCount);

  if (patterns.length === 1) {
    return names.slice(0, unbatchedResultLimit);
  }

  if (names.length <= unbatchedResultLimit) {
    const groups: string[][] = patterns.map(() => []);

    for (const name of names) {
      groups[patterns.findIndex(({ matches }) => matches.has(name))].push(name);
    }

    return groups.flat();
  }

  const selectedNames = new Set<string>();

  for (const pattern of patterns) {
    if (selectedNames.size >= maxAttributes) {
      break;
    }

    sortLikeDefinitionsQuery([pattern], runCount)
      .slice(0, unbatchedResultLimit)
      .forEach((name) => selectedNames.add(name));
  }

  return [...selectedNames];
}

/**
 * The unbatched resolution reads default sized pages while it has at most `maxAttributes` names.
 */
function getUnbatchedResultLimit(maxAttributes: number): number {
  return (
    (Math.floor(maxAttributes / DEFINITIONS_QUERY_DEFAULT_PAGE_SIZE) + 1) *
    DEFINITIONS_QUERY_DEFAULT_PAGE_SIZE
  );
}

/**
 * Whether the order of the request's own query depends on how many of its runs the server counts.
 * The server skips the exact match ranking when a pattern contains `.*` and it resolves more than
 * 100 runs, counting only the runs that exist and are readable; the client knows only the upper
 * bound of that count.
 */
export function hasAmbiguousExactMatchRanking({
  experimentIds,
  patterns,
}: Pick<DefinitionsBatchRequest, 'experimentIds' | 'patterns'>): boolean {
  return (
    new Set(experimentIds).size > SKIP_EXACT_MATCH_PRIORITY_RUN_COUNT &&
    patterns.some(canSkipExactMatchPriority)
  );
}

/**
 * Emulates `order by not_exact_match, path` of a definitions query with the given patterns. The
 * exact match ranking is skipped by the server for more than 100 runs when any pattern contains
 * `.*`, so `runCount` must be the server's count (see hasAmbiguousExactMatchRanking).
 */
export function sortLikeDefinitionsQuery(patterns: MatchedPattern[], runCount: number): string[] {
  const isExactMatchByName = new Map<string, boolean>();

  for (const { matches } of patterns) {
    for (const [name, isExactMatch] of matches) {
      isExactMatchByName.set(name, isExactMatchByName.get(name) === true || isExactMatch);
    }
  }

  const skipExactMatchPriority =
    runCount > SKIP_EXACT_MATCH_PRIORITY_RUN_COUNT &&
    patterns.some(({ pattern }) => canSkipExactMatchPriority(pattern));
  const getRank = (name: string) =>
    skipExactMatchPriority || isExactMatchByName.get(name) === true ? 0 : 1;

  return [...isExactMatchByName.keys()].sort(
    (first, second) => getRank(first) - getRank(second) || compareByCodePoints(first, second),
  );
}

/**
 * Compares like ClickHouse compares UTF-8 strings (bytewise, i.e. by code points). The default JS
 * comparison by UTF-16 code units differs for astral characters, whose surrogates (0xD800-0xDFFF)
 * sort before 0xE000-0xFFFF.
 */
export function compareByCodePoints(first: string, second: string): number {
  const length = Math.min(first.length, second.length);

  for (let index = 0; index < length; index += 1) {
    const firstUnit = first.charCodeAt(index);
    const secondUnit = second.charCodeAt(index);

    if (firstUnit !== secondUnit) {
      return toCodePointOrder(firstUnit) - toCodePointOrder(secondUnit);
    }
  }

  return first.length - second.length;
}

function toCodePointOrder(codeUnit: number): number {
  if (codeUnit >= 0xd800 && codeUnit <= 0xdfff) {
    return codeUnit + 0x2000;
  }

  if (codeUnit >= 0xe000) {
    return codeUnit - 0x800;
  }

  return codeUnit;
}

export function compileCaseInsensitivePattern(pattern: string): RE2JS | undefined {
  try {
    return RE2JS.compile(pattern, RE2JS.CASE_INSENSITIVE);
  } catch {
    return undefined;
  }
}

const ESCAPED_LITERAL = /^[!-/:-@[-`{-~]$/;
const ZERO_WIDTH_OR_CLASS_ESCAPES = new Set(['A', 'b', 'B', 'd', 'D', 's', 'S', 'w', 'W', 'z']);

/**
 * Returns the longest literal that every match of the pattern contains, or undefined when there
 * is none or the pattern uses syntax this simple scan doesn't handle (groups, classes,
 * alternations, counted repetitions, other escapes). Used only to skip regex evaluations of names
 * that can't match.
 */
export function getRequiredLiteral(pattern: string): string | undefined {
  const chars = [...pattern];
  let longestLiteral = '';
  let literal: string[] = [];

  const endLiteral = () => {
    const value = literal.join('');

    if (value.length > longestLiteral.length) {
      longestLiteral = value;
    }

    literal = [];
  };

  for (let index = 0; index < chars.length; index += 1) {
    const char = chars[index];

    if (char === '\\') {
      index += 1;
      const escapedChar = chars[index] ?? '';

      if (ESCAPED_LITERAL.test(escapedChar)) {
        literal.push(escapedChar);
      } else if (ZERO_WIDTH_OR_CLASS_ESCAPES.has(escapedChar)) {
        endLiteral();
      } else {
        return undefined;
      }
    } else if (char === '.' || char === '^' || char === '$' || char === '+') {
      endLiteral();
    } else if (char === '*' || char === '?') {
      // The quantified char is optional.
      literal.pop();
      endLiteral();
    } else if ('()[]{}|'.includes(char)) {
      return undefined;
    } else {
      literal.push(char);
    }
  }

  endLiteral();

  return longestLiteral === '' ? undefined : longestLiteral;
}

const NON_ASCII = /[^\0-\x7f]/;

/**
 * Maps every code point to one char, so that chars equal under RE2 case folding get equal keys:
 * ASCII is lowercased, KELVIN SIGN and LATIN SMALL LETTER LONG S (the only non-ASCII chars
 * folding to ASCII) become `k` and `s`, all other non-ASCII chars become one placeholder.
 * `key(name).includes(key(literal))` is therefore true for every name containing the literal
 * case-insensitively.
 */
export function toCaseInsensitiveKey(value: string): string {
  if (!NON_ASCII.test(value)) {
    return value.toLowerCase();
  }

  let key = '';

  for (const char of value) {
    const codePoint = char.codePointAt(0) ?? 0;

    if (codePoint < 0x80) {
      key += char.toLowerCase();
    } else if (codePoint === 0x212a) {
      key += 'k';
    } else if (codePoint === 0x17f) {
      key += 's';
    } else {
      key += '\u0080';
    }
  }

  return key;
}

function toSortedUnique(values: string[]): string[] {
  return [...new Set(values)].sort();
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}
